import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { NotificationMessage } from '@/components';
import { BlankLayout, StatusLayout } from '@/layout';
import { makeSelectInitialization } from '@/model/auth/selectors';
import { makeSelectApplicationType } from '@/model/global/selectors';
import { ApplicationType } from '@/model/global/types';

const selectAuth = makeSelectInitialization();
const selectApplicationType = makeSelectApplicationType();

const withAuthorizationGuard = (Component: React.FC) => (props: { [k in string]: unknown }): JSX.Element => {
  const { initialized, success, loggedOut, expired, forbidden } = useSelector(selectAuth);
  const applicationType = useSelector(selectApplicationType);
  if (!initialized) {
    return <BlankLayout />;
  }
  if (loggedOut) {
    return (
      <StatusLayout status="success" height={200}>
        <NotificationMessage
          data-test="authorization-loggedOut"
          severity="info"
          title={<FormattedMessage id="common.messages.loggedOut.title" />}
        >
          <FormattedMessage id="common.messages.loggedOut.content" />
        </NotificationMessage>
      </StatusLayout>
    );
  }
  if (expired) {
    return (
      <StatusLayout status="failed" height={200}>
        <NotificationMessage
          data-test="authorization-expired"
          severity="error"
          title={<FormattedMessage id="common.messages.expired.title" />}
        >
          {applicationType === ApplicationType.INVOICE ? (
            <FormattedMessage id="common.messages.expired.invoice" />
          ) : (
            <FormattedMessage id="common.messages.expired.withdrawal" />
          )}
        </NotificationMessage>
      </StatusLayout>
    );
  }

  if (forbidden) {
    return (
      <StatusLayout status="failed" height={220}>
        <NotificationMessage
          data-test="authorization-forbidden"
          severity="error"
          title={<FormattedMessage id="common.messages.forbidden.title" />}
        >
          <FormattedMessage id="common.messages.forbidden.content" />
        </NotificationMessage>
      </StatusLayout>
    );
  }

  return success ? (
    <Component {...props} />
  ) : (
    <StatusLayout status="failed" height={220}>
      <NotificationMessage
        data-test="authorization-error"
        title={<FormattedMessage id="common.errors.unauthorized.title" />}
      >
        <FormattedMessage id="common.errors.unauthorized.content" />
      </NotificationMessage>
    </StatusLayout>
  );
};

export default withAuthorizationGuard;
